import {useSearchParamsContext} from "context/SearchParamsProvider";
import { createContext, useContext, useEffect, useState } from 'react';
import { createStorageEventListener, findLocaleValue, isValidLocaleValue, publishNewLocale } from 'utils/languageUtils';
import { getCookie, setCookie } from 'utils/storageUtils';

// CONTEXT UTILS -------------------------------------------------------------------------------------------------

/** @return {string|null} */
const getCurrentLocale = () => {
  return getCookie('LANG');
};

const setCurrentLocale = (locale) => {
  setCookie('LANG', locale);
};

// CONTEXT PRIVATE -----------------------------------------------------------------------------------------------

/** @type {React.Context<null | { language: string, setLanguage: (any) => void }>} */
const LanguageContext = createContext(null);

/**
 * @param {Map<string, string>} searchParams
 * @return {string}
 */
const loadDefaultLanguage = (searchParams) => {
  if (searchParams.has('l')) {
    const newLocale = findLocaleValue(searchParams.get('l'));
    setCurrentLocale(newLocale);
    publishNewLocale(newLocale);
    return newLocale;
  }
  return findLocaleValue(getCurrentLocale());
};

/**
 * @return {{
 *    language: string,
 *    setLanguage: (any) => void
 * }}
 */
const useInitContext = () => {
  const { data } = useSearchParamsContext();
  const [locale, setLocale] = useState(() => loadDefaultLanguage(data));

  useEffect(() => {
    const storageEventListener = createStorageEventListener((locale) => {
      if (isValidLocaleValue(locale)) {
        setLocale(findLocaleValue(locale));
      }
    });
    window.addEventListener('storage', storageEventListener);
    return () => window.removeEventListener('storage', storageEventListener);
  }, []);

  return {
    language: locale,
    setLanguage: (newLanguage) => {
      const newLocale = findLocaleValue(newLanguage);
      if (newLocale !== locale) {
        setLocale(newLocale);
        setCurrentLocale(newLocale);
        publishNewLocale(newLocale);
      }
    },
  };
};

// CONTEXT PUBLIC -------------------------------------------------------------------------------------------------

/**
 * @return {{ language: string, setLanguage: (any) => void }}
 */
export const useLanguageContext = () => {
  const ctx = useContext(LanguageContext);
  if (ctx == null) {
    throw new Error('Language context not init!');
  }
  return ctx;
};

export const LanguageProvider = ({ children }) => {
  const value = useInitContext();
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
